import styles from './testCaseTemplateModal.module.scss';
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/components/input/input";
import {
  Dropdown,
  DropdownItem
} from "../../shared/components/dropdown/dropdown";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  FieldErrors,
  FieldValues,
  useFieldArray,
  useForm,
  useWatch
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  array,
  boolean,
  number,
  object,
  string
} from "yup";
import {
  EnvironmentServer,
  TemplateParameter,
  TestCaseTemplate
} from "../../types/testCaseTemplateTypes";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import { useGetAllSectionsQuery } from "../../api/testrailService";
import {
  useCreateTestCaseTemplateMutation,
  useDeleteTestCaseTemplateMutation,
  useUpdateTestCaseTemplateMutation
} from "../../api/testCaseTemplateService";
import {
  useDispatch,
  useSelector
} from "react-redux";
import {
  AppDispatch,
  RootState
} from "../../store";
import { Checkbox } from "../../shared/components/checkbox/checkbox";
import { setUpdateModal } from "../../store/slices/mainSlice";
import { MethodType } from "../../shared/components/methodType/methodType";
import { Accordion } from "../../shared/components/accordion/accordion";
import arrowIcon from "../../shared/images/arrow.svg";
import addBlock from "../../shared/images/addBlock.svg";
import removeBlock from "../../shared/images/removeBlock.svg";
import { ParametersInput } from "../../shared/components/parametersInput/parametersInput";
import { useGetAllFolderQuery } from "../../api/testCaseTemplateFolderService";
import { useGetAllEnvironmentsQuery } from "../../api/environmentService";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import { TextArea } from "../../shared/components/textArea/textArea";
import { RadioGroup } from "../../shared/components/radioGroup/radioGroup";
import { useGetAllPrioritiesQuery } from "../../api/priorityService";
import { getNonUniqueIndices } from "../../utils/shared.utils";
import {
  transformTemplateForBackend,
  transformTemplateForUser
} from "../../utils/testCaseUtils";

type TestCaseModalProps = {
  onClose: () => void;
  onCreate: (idCreate: number) => void;
  onUpdate: () => void;
  onDelete: () => void;
  testCaseTemplate?: TestCaseTemplate;
}

export const TestCaseTemplateModal = ({ onClose, onCreate, onUpdate, onDelete, testCaseTemplate }: TestCaseModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const isSandbox = useSelector((state: RootState) => state.authSlice.isSandbox);

  const statusCodes = useSelector((state: RootState) => state.mainSlice.statusCodes);
  const methods = useSelector((state: RootState) => state.mainSlice.methods);

  const selectedVariables = useSelector((state: RootState) => state.mainSlice.selectedVariables);

  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [exportTestrail, setExportTestrail] = useState(!!testCaseTemplate?.sectionId)

  const [requestParameterActiveIndex, setRequestParameterActiveIndex] = useState<number | undefined>(0);
  const [responseParameterActiveIndex, setResponseParameterActiveIndex] = useState<number | undefined>(0);
  const [requestHeaderActiveIndex, setRequestHeaderActiveIndex] = useState<number | undefined>(0);

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const { data: environments } = useGetAllEnvironmentsQuery("");
  const { data: sections} = useGetAllSectionsQuery("", { skip: !exportTestrail || isSandbox, refetchOnMountOrArgChange: true });
  const { data: folders} = useGetAllFolderQuery("");
  const { data: priorities } = useGetAllPrioritiesQuery("");

  const [createTestCaseTemplateMutation, { data: createData, isSuccess: isSuccessCreateTestCaseTemplate, isLoading: isLoadingCreate }] = useCreateTestCaseTemplateMutation();
  const [updateTestCaseTemplateMutation, { isSuccess: isSuccessUpdateTestCaseTemplate, isLoading: isLoadingUpdate }] = useUpdateTestCaseTemplateMutation();
  const [deleteTestCaseTemplateMutation, { isSuccess: isSuccessDeleteTestCaseTemplate, isLoading: isLoadingDelete }] = useDeleteTestCaseTemplateMutation();

  const disabledExportCheckbox = useMemo(() => {
    return isSandbox || (!!testCaseTemplate?.id && !!testCaseTemplate.sectionId);
  }, [testCaseTemplate]);

  const schema = object({
    endpoint: string().typeError(t("common.notValid")),
    name: string().typeError(t("common.notValid")).required(t("common.notValid")),
    maximumExecutionTimeout: number().typeError(t("common.notValid")).required(t("common.notValid"))
      .max(10000, t("common.notValid")).min(1, t("common.notValid")),
    methodId: number().required(),
    statusCodeId: number().required(),
    sectionId: exportTestrail ? number().required(t("common.notValid")) : number().nullable(),
    description: string(),
    body: string(),
    expectedBody: string(),
    testCaseTemplateFolderId: number().required(),
    isPositive: string().required(),
    priorityId: number().required(),
    isJsonSchemaValidation: boolean().required(),
    requestHeaders: array().required().of(
      object().shape({
        externalId: number(),
        name: string().required(t("common.notValid")),
        value: string()
      })
    ).test("unique", (value) => value.length === new Set(value.map(item => item.name))?.size),
    requestParameters: array().required().of(
      object().shape({
        externalId: number(),
        name: string().required(t("common.notValid")),
        value: string()
      })
    ).test("unique", (value) => value.length === new Set(value.map(item => item.name))?.size),
    responseParameters: array().required().of(
      object().shape({
        externalId: number(),
        name: string().required(t("common.notValid")),
        value: string()
      })
    ).test("unique", (value) => value.length === new Set(value.map(item => item.name))?.size),
    environmentServers: array().required().of(
      object().shape({
        externalId: number(),
        environmentId: number().required(),
        serverId: number().required()
      })
    )
  });

  const defaultValues = useMemo(() => {
    const templateForUser = transformTemplateForUser(selectedVariables, testCaseTemplate);

    let requestHeaders: Array<{externalId?: number, name: string, value?: string}> = [];
    let requestParameters: Array<{externalId?: number, name: string, value?: string}> = [];
    let responseParameters: Array<{externalId?: number, name: string, value?: string}> = [];
    const environmentServers = environments?.map(env => {
      const templateServer = templateForUser?.environmentServers.find(item => item.environmentId === env.id);
      return {
        externalId: templateServer?.id,
        environmentId: env.id,
        serverId: templateServer?.serverId || 0
      };
    }) || [];

    if (templateForUser) {
      if (templateForUser.id) {
        requestHeaders = templateForUser.requestHeaders.map(item => ({
          externalId: item.id,
          name: item.name,
          value: item.value
        }));
        requestParameters = templateForUser.requestParameters.map(item => ({
          externalId: item.id,
          name: item.name,
          value: item.value
        }));
        responseParameters = templateForUser.responseParameters.map(item => ({
          externalId: item.id,
          name: item.name,
          value: item.value
        }));
      } else {
        if (templateForUser.requestHeaders.length !== 0) {
          requestHeaders = templateForUser.requestHeaders.map(item => ({
            externalId: item.id,
            name: item.name,
            value: item.value
          }));
        }
        if (templateForUser.requestParameters.length !== 0) {
          requestParameters = templateForUser.requestParameters.map(item => ({
            externalId: item.id,
            name: item.name,
            value: item.value
          }));
        }
        if (templateForUser.responseParameters.length !== 0) {
          responseParameters = templateForUser.responseParameters.map(item => ({
            externalId: item.id,
            name: item.name,
            value: item.value
          }));
        }
      }
    }

    const methodName = methods?.find(method => method.id === templateForUser?.methodId)?.name;
    const body = methodName === 'GET' ? templateForUser?.payload : templateForUser?.body;

    return {
      endpoint: templateForUser?.endpoint || "",
      name: templateForUser?.name || "",
      maximumExecutionTimeout: templateForUser?.maximumExecutionTimeout || 0,
      methodId: templateForUser?.methodId || methods[0].id || 0,
      statusCodeId: templateForUser?.statusCodeId || statusCodes[0].id || 0,
      sectionId: templateForUser?.sectionId || null,
      description: templateForUser?.description || "",
      body: body || "",
      expectedBody: templateForUser?.expectedBody || "",
      testCaseTemplateFolderId: templateForUser?.testCaseTemplateFolderId || folders?.[0].id || 0,
      isPositive: templateForUser?.isPositive ? "1" : "0",
      priorityId: templateForUser?.priorityId || priorities?.[0]?.id || 0,
      isJsonSchemaValidation: templateForUser?.isJsonSchemaValidation || false,
      requestHeaders,
      requestParameters,
      responseParameters,
      environmentServers,
    };
  }, [selectedVariables, testCaseTemplate, methods, statusCodes, folders, environments, priorities]);

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    mode: "onChange"
  });

  const { fields: environmentServersField } = useFieldArray({ control, name: "environmentServers" });

  const {
    fields: requestHeadersFields,
    append: appendRequestHeaders,
    remove: removeRequestHeaders
  } = useFieldArray({control, name: "requestHeaders"});
  const requestHeadersWatch = useWatch({control, name: "requestHeaders"});


  const {
    fields: requestParametersFields,
    append: appendRequestParameters,
    remove: removeRequestParameters
  } = useFieldArray({control, name: "requestParameters"});
  const requestParametersWatch = useWatch({control, name: "requestParameters"});

  const {
    fields: responseParametersFields,
    append: appendResponseParameters,
    remove: removeResponseParameters
  } = useFieldArray({control, name: "responseParameters"});

  const responseParametersWatch = useWatch({control, name: "responseParameters"});

  useEffect(() => {
    if (priorities) {
      reset(defaultValues);
    }
  }, [priorities, reset]);

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  useEffect(() => {
    if (isSuccessCreateTestCaseTemplate && !!createData) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onCreate(createData);
    }
  }, [isSuccessCreateTestCaseTemplate, createData]);

  useEffect(() => {
    if (isSuccessUpdateTestCaseTemplate) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onUpdate();
    }
  }, [isSuccessUpdateTestCaseTemplate]);

  useEffect(() => {
    if (isSuccessDeleteTestCaseTemplate) {
      dispatch(setUpdateModal({ show: true, isDelete: true }));
      onDelete();
    }
  }, [isSuccessDeleteTestCaseTemplate]);

  const getRequestHeaders = useCallback((value: { requestHeaders: FieldValues[] }): Array<TemplateParameter> => {
    const defaultValue = value.requestHeaders.map(item => ({
      id: item.externalId,
      name: item.name,
      value: item.value || "",
    }));
    if (!!testCaseTemplate?.id) {
      const result: Array<TemplateParameter> = [];
      testCaseTemplate.requestHeaders.forEach(parameter => {
        const find = defaultValue.find(item => item.id === parameter.id);
        if (!find) {
          result.push({...parameter, isDeleted: true});
        }
      });
      result.push(...defaultValue);
      return result;
    } else {
      return defaultValue;
    }
  }, [testCaseTemplate]);

  const getRequestParameters = useCallback((value: { requestParameters: FieldValues[] }): Array<TemplateParameter> => {
    const defaultValue = value.requestParameters.map(item => ({
      id: item.externalId,
      name: item.name,
      value: item.value,
    }));
    if (!!testCaseTemplate?.id) {
      const result: Array<TemplateParameter> = [];
      testCaseTemplate.requestParameters.forEach(parameter => {
        const find = defaultValue.find(item => item.id === parameter.id);
        if (!find) {
          result.push({...parameter, isDeleted: true});
        }
      });
      result.push(...defaultValue);
      return result;
    } else {
      return defaultValue;
    }
  }, [testCaseTemplate]);

  const getResponseParameters = useCallback((value: { responseParameters: any[] }): Array<TemplateParameter> => {
    const defaultValue = value.responseParameters.map(item => ({
      id: item.externalId,
      name: item.name,
      value: item.value,
    }));
    if (!!testCaseTemplate?.id) {
      const result: Array<TemplateParameter> = [];
      testCaseTemplate.responseParameters.forEach(parameter => {
        const find = defaultValue.find(item => item.id === parameter.id);
        if (!find) {
          result.push({...parameter, isDeleted: true});
        }
      });
      result.push(...defaultValue);
      return result;
    } else {
      return defaultValue;
    }
  }, [testCaseTemplate]);

  const errorHandler = useCallback((error: FieldErrors) => {
    if (!!error.requestHeaders) {
      setRequestHeaderActiveIndex(0);
    }
    if (!!error.requestParameters) {
      setRequestParameterActiveIndex(0);
    }
    if (!!error.responseParameters) {
      setResponseParameterActiveIndex(0);
    }
  }, [setRequestHeaderActiveIndex, setRequestParameterActiveIndex, setResponseParameterActiveIndex]);

  const onClickSave = useCallback((): void => {
    handleSubmit((value) => {
      const method = methods?.find(item => item.id === value.methodId);
      const body = method?.name === "GET" ? undefined : value.body;
      const payload = method?.name === "GET" ? value.body : undefined;
      let sectionId = exportTestrail && !!value.sectionId ? value.sectionId : undefined;
      const newTestCaseTemplate: TestCaseTemplate = {
        endpoint: value.endpoint,
        name: value.name,
        body,
        payload,
        description: value.description,
        methodId: value.methodId,
        maximumExecutionTimeout: value.maximumExecutionTimeout,
        statusCodeId: value.statusCodeId,
        expectedBody: value.expectedBody,
        testCaseTemplateFolderId: value.testCaseTemplateFolderId,
        isPositive: !!+value.isPositive,
        priorityId: value.priorityId,
        isJsonSchemaValidation: value.isJsonSchemaValidation,
        sectionId,
        childTestCases: !!testCaseTemplate?.id ? testCaseTemplate.childTestCases : [],
        requestHeaders: getRequestHeaders(value),
        requestParameters: getRequestParameters(value),
        responseParameters: getResponseParameters(value),
        environmentServers: []
      };
      if (!!testCaseTemplate?.id) {
        newTestCaseTemplate.id = testCaseTemplate.id;

        value.environmentServers.forEach(server => {
          const findServer = testCaseTemplate.environmentServers.find(item => item.id === server.externalId);
          if (!!findServer) {
            const newServer: EnvironmentServer = {
              id: findServer.id,
              serverId: server.serverId,
              environmentId: findServer.environmentId,
              isDeleted: server.serverId === 0
            };
            newTestCaseTemplate.environmentServers.push(newServer);
          } else if (server.serverId !== 0) {
            newTestCaseTemplate.environmentServers.push({
              environmentId: server.environmentId,
              serverId: server.serverId
            });
          }
        });
        const transformTemplate = transformTemplateForBackend(selectedVariables, newTestCaseTemplate);
        updateTestCaseTemplateMutation(transformTemplate);
      } else {
        newTestCaseTemplate.environmentServers = value.environmentServers
          .filter(item => item.serverId !== 0)
          .map(item => ({
            environmentId: item.environmentId,
            serverId: item.serverId
          }));
        const transformTemplate = transformTemplateForBackend(selectedVariables, newTestCaseTemplate);
        createTestCaseTemplateMutation(transformTemplate);
      }
    }, (error) => {
      errorHandler(error);
    })();
  }, [testCaseTemplate, exportTestrail, handleSubmit, getRequestHeaders, getRequestParameters, getResponseParameters, updateTestCaseTemplateMutation, createTestCaseTemplateMutation, errorHandler]);

  const onDeleteClick = useCallback((): void => {
    setShowDeleteMessage(true);
  }, [setShowDeleteMessage])

  const closeDeleteMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm && !!testCaseTemplate?.id) {
      deleteTestCaseTemplateMutation(testCaseTemplate.id);
    }
    setShowDeleteMessage(false);
  }

  const onChangeExportTestrail = useCallback(() => {
    if (!disabledExportCheckbox) {
      setExportTestrail((exportTestrail) => !exportTestrail);
    }
  }, [disabledExportCheckbox, setExportTestrail]);

  const getRequestHeadersErrorName = (index: number): string | undefined => {
    if (getNonUniqueIndices(requestHeadersWatch, "name").includes(index)) {
      return t("common.notUnique");
    } else {
      return !!errors.requestHeaders?.[index]?.name ? errors.requestHeaders?.[index]?.name?.message : "";
    }
  }

  const getRequestParametersErrorName = (index: number): string | undefined => {
    if (getNonUniqueIndices(requestParametersWatch, "name").includes(index)) {
      return t("common.notUnique");
    } else {
      return !!errors.requestParameters?.[index]?.name ? errors.requestParameters?.[index]?.name?.message : "";
    }
  }

  const getResponseParametersErrorName = (index: number): string | undefined => {
    if (getNonUniqueIndices(responseParametersWatch, "name").includes(index)) {
      return t("common.notUnique");
    } else {
      return !!errors.responseParameters?.[index]?.name ? errors.responseParameters?.[index]?.name?.message : "";
    }
  }

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose();
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);

  const getServersDropdown = (environmentId: number): DropdownItem[] => {
    const result: DropdownItem[] = [{
      id: 0,
      name: "",
      title: ""
    }]
    const environment = environments?.find(item => item.id === environmentId);
    result.push(...environment?.servers.map(item => ({
      id: item.id as number,
      name: item.name,
      title: item.name
    })) || []);
    return result;
  }

  const getEnvironmentName = useCallback((environmentId: number): string => {
    return environments?.find(item => item.id === environmentId)?.name || "";
  }, [environments]);

  const addRequestHeader = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    appendRequestHeaders([{name: "", value: ""}]);
    setRequestHeaderActiveIndex(0);
  }, [appendRequestHeaders, setRequestHeaderActiveIndex]);

  const addRequestParameter = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    appendRequestParameters([{name: "", value: ""}]);
    setRequestParameterActiveIndex(0);
  }, [appendRequestParameters, setRequestParameterActiveIndex]);

  const addResponseParameter = useCallback((e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    appendResponseParameters([{name: "", value: ""}]);
    setResponseParameterActiveIndex(0);
  }, [appendResponseParameters, setResponseParameterActiveIndex]);

  const getMethodsDropdownItems: DropdownItem[] = useMemo(() => {
    return methods.map(item => ({
      id: item.id,
      name: <MethodType methodId={item.id}/>,
      title: item.name
    }));
  }, [methods]);

  const getStatusesDropdownItems: DropdownItem[] = useMemo(() => {
    return statusCodes.map(item => ({
      id: item.id,
      name: `${item.code} ${item.name}`,
      title: `${item.code} ${item.name}`
    }));
  }, [statusCodes]);

  const getSectionDropdownItems: DropdownItem[] = useMemo(() => {
    if (!!sections && exportTestrail) {
      return sections.map(item => ({
        id: item.id,
        name: item.name,
        title: item.name
      }))
    } else {
      return []
    }
  }, [sections, exportTestrail]);

  const getFolderDropdownItems: DropdownItem[] = useMemo(() => {
    if (!!folders) {
      return folders.map(item => ({
        id: item.id as number,
        name: item.name,
        title: item.name
      }))
    } else {
      return []
    }
  }, [folders]);

  const getPriorityDropdownItems: DropdownItem[] = useMemo(() => {
    if (!!priorities) {
      return priorities.map(item => ({
        id: item.id,
        name: item.name,
        title: item.name
      }))
    } else {
      return []
    }
  }, [priorities]);

  return (
    <>
      <ModalWindow onClose={onCancel}
                   width={814}
                   title={!!testCaseTemplate?.id ? t("testCaseTemplate.edit") : t("testCaseTemplate.add")}
                   children={
                     <>
                       <div className={styles.container}>
                         <div className={styles.mainInfo}>
                           <div className={styles.inputs}>
                             <div className={styles.inputContainer}>
                               <span className={styles.required}>{t("testCaseTemplate.name")}</span>
                               <div className={styles.input}>
                                 <Input control={control}
                                        name={"name"}
                                        maxLength={100}
                                        error={!!errors.name ? errors.name.message : ""}/>
                               </div>
                             </div>
                             <div className={styles.radioGroup}>
                               <RadioGroup control={control}
                                           name="isPositive"
                                           options={[
                                             { title: t("testCaseTemplate.positive"), value: "1" },
                                             { title: t("testCaseTemplate.negative"), value: "0" }
                                           ]}/>
                             </div>
                             <div className={`${styles.textAreaContainer} ${styles.description}`}>
                               <div className={styles.title}>
                                 {t("common.description")}
                               </div>
                               <TextArea control={control}
                                         name={"description"}
                                         maxLength={5700}/>
                             </div>
                             <div className={styles.checkbox}
                                  onClick={onChangeExportTestrail}>
                               <Checkbox isChecked={exportTestrail} disabled={disabledExportCheckbox}/>
                               <span>{t("testCase.exportToTestRail")}</span>
                             </div>
                             <div className={styles.inputContainer}>
                               <span>{t("testCase.section")}</span>
                               <div className={styles.input}>
                                 <Dropdown control={control}
                                           isInput
                                           fontPopupClass={styles.fontDropdownPopup}
                                           items={getSectionDropdownItems}
                                           error={!!errors.sectionId ? errors.sectionId.message : ""}
                                           name={"sectionId"}
                                           disabled={!exportTestrail || isSandbox}/>
                               </div>
                             </div>
                           </div>

                           <div className={styles.inputs}>
                             <div className={styles.inputContainer}>
                               <span>{t("testCase.method")}</span>
                               <div className={styles.input}>
                                 <Dropdown control={control}
                                           fontPopupClass={styles.fontDropdownPopup}
                                           items={getMethodsDropdownItems}
                                           name={"methodId"}/>
                               </div>
                             </div>
                             <div className={styles.inputContainer}>
                               <span>{t("testCase.expectedStatus")}</span>
                               <div className={styles.input}>
                                 <Dropdown control={control}
                                           fontPopupClass={styles.fontDropdownPopup}
                                           items={getStatusesDropdownItems}
                                           name={"statusCodeId"}/>
                               </div>
                             </div>
                             <div className={styles.inputContainer}>
                               <span className={styles.required}>{t("common.maxExecutionTimeout")}</span>
                               <div className={styles.input}>
                                 <Input control={control}
                                        type={"number"}
                                        maxLength={4}
                                        name={"maximumExecutionTimeout"}
                                        error={!!errors.maximumExecutionTimeout ? errors.maximumExecutionTimeout.message : ""}/>
                               </div>
                             </div>
                             <div className={styles.inputContainer}>
                               <span className={styles.required}>{t("testCaseTemplate.folder")}</span>
                               <div className={styles.input}>
                                 <Dropdown control={control}
                                           fontPopupClass={styles.fontDropdownPopup}
                                           items={getFolderDropdownItems}
                                           error={!!errors.testCaseTemplateFolderId ? errors.testCaseTemplateFolderId.message : ""}
                                           name={"testCaseTemplateFolderId"}/>
                               </div>
                             </div>
                             <div className={styles.inputContainer}>
                               <span className={styles.required}>{t("testCaseTemplate.priority")}</span>
                               <div className={styles.input}>
                                 <Dropdown control={control}
                                           fontPopupClass={styles.fontDropdownPopup}
                                           items={getPriorityDropdownItems}
                                           name={"priorityId"}/>
                               </div>
                             </div>
                           </div>
                         </div>

                         <div className={styles.environments}>
                           <Accordion activeIndex={0}
                                      disabledAutoScroll
                                      activeClassName={styles.active}
                                      maxHeight={400}
                                      data={[{
                                        title: (
                                          <div className={styles.accordionTitle}>
                                            <img src={arrowIcon} alt=""
                                                 className={styles.arrow}/>
                                            <span>{t("testCaseTemplate.environments")}</span>
                                          </div>
                                        ),
                                        child: (
                                          <div className={styles.accordionChild}>
                                            <div className={styles.inputs}>
                                              <div className={styles.inputContainer}>
                                                <span>{t("common.name")}</span>
                                              </div>
                                              <div className={styles.inputContainer}>
                                                <span>{t("common.server")}</span>
                                              </div>
                                            </div>
                                            {environmentServersField?.map(((environment, index) => (
                                              <div className={styles.inputs} key={environment.id}>
                                                <div className={styles.inputContainer}>
                                                  <span>{getEnvironmentName(environment.environmentId)}</span>
                                                </div>
                                                <div className={styles.inputContainer}>
                                                  <div className={styles.input}>
                                                    <Dropdown items={getServersDropdown(environment.environmentId)}
                                                              name={`environmentServers.${index}.serverId`}
                                                              fontPopupClass={styles.fontDropdownPopup}
                                                              control={control}/>
                                                  </div>
                                                </div>
                                              </div>
                                            )))}
                                          </div>
                                        )
                                      }]}/>
                         </div>
                         <div className={styles.parameters}>
                           <Accordion activeIndex={requestParameterActiveIndex}
                                      onChangeActive={setRequestParameterActiveIndex}
                                      activeClassName={styles.active}
                                      maxHeight={400}
                                      disabledAutoScroll
                                      data={[{
                             title: (
                               <div className={styles.accordionTitle}>
                                 <img src={arrowIcon} alt=""
                                      className={styles.arrow}/>
                                 <span>{t("testCaseTemplate.requestParameters")}</span>
                                 <div className={styles.addRemoveBlock} onClick={addRequestParameter}>
                                   <img src={addBlock} alt=""/>
                                 </div>
                               </div>
                             ),
                             child: (
                               <div className={styles.accordionChild}>
                                 {requestParametersFields.map((field, index) => (
                                   <div className={`${styles.inputs} ${index === 0 ? styles.first : ""}`}
                                        key={field.id}>
                                     <div className={styles.inputContainer}>
                                       <span className={styles.required}>{t("common.name")}</span>
                                       <div className={styles.input}>
                                         <Input control={control}
                                                maxLength={128}
                                                name={`requestParameters.${index}.name`}
                                                error={getRequestParametersErrorName(index)}/>
                                       </div>
                                     </div>
                                     <div className={`${styles.inputContainer} ${styles.value}`}>
                                       <span className={styles.optional}>{t("testCaseTemplate.value")}</span>
                                       <div className={styles.input}>
                                         <Input control={control}
                                                maxLength={1000}
                                                name={`requestParameters.${index}.value`}
                                                error={!!errors.requestParameters?.[index]?.value ? errors.requestParameters?.[index]?.value?.message : ""}/>
                                       </div>
                                     </div>
                                     <div className={styles.addRemoveBlock}
                                          onClick={() => removeRequestParameters(index)}>
                                       <img src={removeBlock} alt=""/>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                             )
                           }]}/>
                         </div>
                         <div className={styles.parameters}>
                           <Accordion activeIndex={responseParameterActiveIndex}
                                      onChangeActive={setResponseParameterActiveIndex}
                                      maxHeight={400}
                                      activeClassName={styles.active}
                                      disabledAutoScroll
                                      data={[{
                             title: (
                               <div className={styles.accordionTitle}>
                                 <img src={arrowIcon} alt=""
                                      className={styles.arrow}/>
                                 <span>{t("testCaseTemplate.responseParameters")}</span>
                                 <div className={styles.addRemoveBlock}
                                      onClick={addResponseParameter}>
                                   <img src={addBlock} alt=""/>
                                 </div>
                               </div>
                             ),
                             child: (
                               <div className={styles.accordionChild}>
                                 {responseParametersFields.map((field, index) => (
                                   <div className={`${styles.inputs} ${index === 0 ? styles.first : ""}`}
                                        key={field.id}>
                                     <div className={styles.inputContainer}>
                                       <span className={styles.required}>{t("common.name")}</span>
                                       <div className={styles.input}>
                                         <Input control={control}
                                                maxLength={128}
                                                name={`responseParameters.${index}.name`}
                                                error={getResponseParametersErrorName(index)}/>
                                       </div>
                                     </div>
                                     <div className={`${styles.inputContainer} ${styles.value}`}>
                                       <span className={styles.optional}>{t("testCaseTemplate.value")}</span>
                                       <div className={styles.input}>
                                         <Input control={control}
                                                maxLength={1000}
                                                name={`responseParameters.${index}.value`}
                                                error={!!errors.responseParameters?.[index]?.value ? errors.responseParameters?.[index]?.value?.message : ""}/>
                                       </div>
                                     </div>
                                     <div className={styles.addRemoveBlock}
                                          onClick={() => removeResponseParameters(index)}>
                                       <img src={removeBlock} alt=""/>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                             )
                           }]}/>
                         </div>
                         <div className={`${styles.parameters} ${styles.headers}`}>
                           <Accordion activeIndex={requestHeaderActiveIndex}
                                      onChangeActive={setRequestHeaderActiveIndex}
                                      activeClassName={styles.active}
                                      maxHeight={400}
                                      disabledAutoScroll
                                      data={[{
                             title: (
                               <div className={styles.accordionTitle}>
                                 <img src={arrowIcon} alt=""
                                      className={styles.arrow}/>
                                 <span>{t("testCaseTemplate.headers")}</span>
                                 <div className={styles.addRemoveBlock}
                                      onClick={addRequestHeader}>
                                   <img src={addBlock} alt=""/>
                                 </div>
                               </div>
                             ),
                             child: (
                               <div className={styles.accordionChild}>
                                 {requestHeadersFields.map((field, index) => (
                                   <div className={`${styles.inputs} ${index === 0 ? styles.first : ""}`}
                                        key={field.id}>
                                     <div className={styles.inputContainer}>
                                       <span className={styles.required}>{t("common.name")}</span>
                                       <div className={styles.textAreaContainer}>
                                         <TextArea control={control}
                                                   maxLength={128}
                                                   name={`requestHeaders.${index}.name`}
                                                   error={getRequestHeadersErrorName(index)}/>
                                       </div>
                                     </div>
                                     <div className={`${styles.inputContainer} ${styles.value}`}>
                                       <ParametersInput control={control}
                                                        name={`requestHeaders.${index}.value`}
                                                        title={(<span className={styles.optional}>{t("testCaseTemplate.value")}</span>)}
                                                        showVariables={true}
                                                        params={requestParametersWatch.filter(item => !!item.name)}
                                                        maxLength={1000}
                                                        error={!!errors.requestHeaders?.[index]?.value ? errors.requestParameters?.[index]?.value?.message : ""}/>
                                     </div>
                                     <div className={styles.addRemoveBlock}
                                          onClick={() => removeRequestHeaders(index)}>
                                       <img src={removeBlock} alt=""/>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                             )
                           }]}/>
                         </div>
                         <div className={styles.endpoint}>
                           <ParametersInput control={control}
                                            showVariables={true}
                                            showRequestParameters={true}
                                            title={t("testCase.endpoint")}
                                            name={"endpoint"}
                                            params={requestParametersWatch.filter(item => !!item.name)}
                                            maxLength={255}
                                            error={!!errors.endpoint ? errors.endpoint.message : ""}/>
                         </div>
                         <div className={styles.textAreaContainer}>
                           <ParametersInput control={control}
                                            showVariables={true}
                                            showRequestParameters={true}
                                            title={`${t("common.body")} / ${t("common.parametersString")}`}
                                            name={"body"}
                                            params={requestParametersWatch.filter(item => !!item.name)}
                                            maxLength={1000000}/>
                         </div>
                         <div className={styles.textAreaContainer}>
                           <ParametersInput control={control}
                                            showConstantsParameters={true}
                                            nameJsonValidation={"isJsonSchemaValidation"}
                                            showVariables={true}
                                            title={t("testCase.expectedResult")}
                                            name={"expectedBody"}
                                            params={responseParametersWatch.filter(item => !!item.name)}
                                            maxLength={65535}/>
                         </div>
                       </div>
                       <div className={styles.buttonsContainer}>
                         <div className={styles.buttons}>
                           {!!testCaseTemplate?.id && (
                             <button className={styles.delete}
                                     disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                     onClick={onDeleteClick}>{t("common.delete")}</button>
                           )}
                         </div>
                         <div className={styles.buttons}>
                           <button className={styles.cancel}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={() => onCancel()}>{t("common.cancel")}</button>
                           <button className={styles.save}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onClickSave}>{t("common.save")}</button>
                         </div>
                       </div>
                     </>
                   }
      />
      {showDeleteMessage && (
        <MessageWindow onClose={closeDeleteMessageWindow}
                       title={t("testCase.delete")}
                       description={t("testCase.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>
  );
}